import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { TAccount } from "../account";
import { TOrganization, TOrganizationData } from "../organizations";
import { handleGetDataFromLocalStorage } from "../../../utils/miscellaneous";
import { localStorageConstants } from "../../../utils/constants";
import { TPaidInstallments } from "../../../components/ui-v2/Modal/Forms/AddPayable";
import {
  fetchExportedIssuedInvoice,
  fetchExportedIssuedInvoices,
  fetchInvoices,
  fetchIssuedInvoices,
  fetchMemberships,
  fetchModalInvoices,
  fetchModalPaymentSlips,
  fetchPaymentSlips,
} from "../../actions";

import { TIssuedInvoice } from "../../../pages/IssuedInvoices/mock";
import { TIssuedInvoicesExport } from "../../../pages/IssuedInvoices/Export/mock";
import { TIssuedInvoiceExport } from "../../../pages/IssuedInvoices/Export/Details/mock";

export type TToast = {
  shouldShow: boolean;
  message: {
    title: string;
    description: string;
    link?: { url: string };
    linkText?: {
      text: string;
      url?: string;
      actionId?: string;
    };
  };
  variant: "success" | "error" | "information" | "loading";
};

type TInvoiceIssuer = {
  name: string;
  document_number: string;
};

export type TInvoiceManifestastionStatus = 10 | 20 | 30 | 40;
// 10 - ciente
// 20 - confirmada
// 30 - desconhecida
// 40 - recusada

export type TInvoiceEntryStatus = 10 | 20 | 30; // 10 - pendente, 20 - finalizada, 30 recusada

export type TInvoiceType = 10 | 20; // 10 - produto, 20 - serviço

export type TInvoiceSituation = 1 | 2 | 3; // 1 - Uso autorizado, 2 - Uso denegado, 3 - NF-e Cancelada

export type TNexaasState = 10 | 20 | 30 | 40 | 50 | 60;
// 10 - entrada não criada
// 20 - entrada criada e finalizada
// 30 - entrada criada
// 40 - verificação com erros
// 50 - lançando estoques
// 60 - entrada finalizada

export type TDuplicate = {
  installment_number: number;
  amount: string;
  due_date: string;
};

export type TInvoice = {
  access_key: string;
  amount: string;
  date_issue: string;
  issuer: TInvoiceIssuer;
  launch_status: TInvoiceEntryStatus;
  manifestation: TInvoiceManifestastionStatus;
  number: number;
  uuid: string;
  file_xml: string | null;
  file_nfe: string | null;
  goods_receipt_url: string | null;
  accounts_payable_url: string | null;
  fintera_id: string | null;
  boletos_exists: boolean;
  label: string;
  invoice_type: TInvoiceType;
  nexaas_state: TNexaasState;
  nexaas_finished_at: string | null;
  duplicates: TDuplicate[];
  situation: TInvoiceSituation;
};

export type TInvoiceData = {
  count: number;
  results: TInvoice[];
  current_page: number;
  pages: { [key: string]: string }[];
};

export type TIssuedInvoiceData = {
  count: number;
  results: TIssuedInvoice[];
  current_page: number;
  pages: { [key: string]: string }[];
};

export type TExportIssuedInvoiceData = {
  count: number;
  results: TIssuedInvoicesExport[];
  current_page: number;
  pages: { [key: string]: string }[];
};

export type TInvoiceUpdatedAt = { responseStatus: number; date: string };

export type TPaymentSlip = {
  uuid: string;
  number: string;
  bar_code: string;
  amount: string;
  payer_name: string;
  payer_document: string;
  provider_name: string;
  provider_document: string;
  process_date: string;
  due_date: string;
  source: string;
  launch_status: TInvoiceEntryStatus;
  invoices_exists: boolean;
  file_boleto: string;
  accounts_payable_url: string | null;
  fintera_id: string | null;
  label: string;
  user: {
    uuid: string;
    email: string;
    first_name: string;
    last_name: string;
  };
};

export type TPaymentSlipData = {
  count: number;
  results: TPaymentSlip[];
  current_page: number;
  pages: { [key: string]: string }[];
};

export type TPaymentSlipUpdatedAt = { responseStatus: number; date: string };

export type TNexaasProduct = {
  index: number;
  sku: string | null;
  supplier_code: string;
  supplier_sku: string;
  description: string;
  ean: string;
  quantity: string;
  measure: string;
  verified_quantity: null | string;
  verified_measure: null | string;
  conversion_factor: {
    id: number;
    sales_measure: string;
    sales_quantity: string;
    purchase_measure: string;
    purchase_quantity: string;
  } | null;
  batch_name?: string | null;
  batch_manufacturing_date?: string | null;
  batch_expiration_date?: string | null;
  order_number?: string | null;
  reason?: string;
};

export type TGlobalState = {
  toast: TToast;
  toastActionId?: string;
  logoutModal: { shouldShow: boolean };
  /**
   * Currently selected account for context.
   * Source: Set by user navigation or selection. Fetched from /accounts endpoint.
   */
  selectedAccount: TAccount | null;
  /**
   * @deprecated
   * TODO: Remove selectedOrganizationWithPermissions. Use selectOrganization and fetch permissions as needed.
   * Migration: Refactor all consumers to use selectOrganization and fetch/check permissions via selector or API call.
   */
  selectedOrganizationWithPermissions: TOrganization | null;
  /**
   * Canonical selected organization for all navigation, API calls, and context-sensitive logic.
   * Migration: Ensure all consumers use this for organization context.
   */
  selectOrganization: TOrganization | null;
  /**
   * List of recently accessed accounts for quick switching.
   * Source: Updated on account selection, persisted in localStorage. Fetched from /accounts.
   * TODO: Confirm persistency logic and localStorage sync during migration.
   */
  lastAccessedAccounts: TAccount[];
  /**
   * List of recently accessed organizations for quick switching.
   * Source: Updated on organization selection, persisted in localStorage. Fetched from /organizations.
   * TODO: Confirm persistency logic and localStorage sync during migration.
   */
  lastAccessedOrganizations: TOrganization[];
  /**
   * List of organizations user is allowed to access. Used for dropdowns, quick access, etc.
   * TODO: Remove if not actively used in UI or logic.
   */
  allowedOrganizations: TOrganization[] | null;
  /**
   * Detailed data for selected organization.
   * Source: Fetched on organization selection from /organizations/:id.
   */
  organizationData: TOrganizationData | null;
  /**
   * Membership details for current user/org.
   * Source: Fetched on login or org change from /memberships.
   */
  paymentSlipState: TPaymentSlipData;
  modalPaymentSlipState: TPaymentSlipData;
  paymentSlipUpdatedAt: TPaymentSlipUpdatedAt;
  pendingPaymentSlipsCount: number;
  donePaymentSlipsCount: number;
  rejectPaymentSlipsCount: number;
  invoiceState: TInvoiceData;
  issuedInvoiceState: TIssuedInvoiceData;
  exportedIssuedInvoiceState: TExportIssuedInvoiceData;
  exportedIssuedInvoice: TIssuedInvoiceExport | null;
  modalInvoiceState: TInvoiceData;
  invoiceUpdatedAt: TInvoiceUpdatedAt;
  pendingInvoicesCount: number;
  doneInvoicesCount: number;
  rejectInvoicesCount: number;
  allPaidElements: TPaidInstallments[];
  attachedInvoices: TInvoice[];
  attachedPaymentSlips: TPaymentSlip[];
  updatedInvoice?: TInvoice;
};

type TInitialState = {
  isLoadingInvoices: boolean;
  isLoadingIssuedInvoices: boolean;
  isLoadingExportedIssuedInvoices: boolean;
  isLoadingPaymentSlips: boolean;
  isLoadingMemberships: boolean;
  isLoadingModalInvoices: boolean;
  isLoadingModalPaymentSlips: boolean;
  isRestoringQuerystring: boolean;
  data: TGlobalState;
};

const initialState: TInitialState = {
  isLoadingInvoices: true,
  isLoadingIssuedInvoices: true,
  isLoadingExportedIssuedInvoices: true,
  isLoadingPaymentSlips: true,
  isLoadingMemberships: true,
  isLoadingModalInvoices: true,
  isLoadingModalPaymentSlips: true,
  isRestoringQuerystring: false,
  data: {
    toastActionId: undefined,
    toast: {
      variant: "success",
      shouldShow: false,
      message: { title: "", description: "" },
    },
    logoutModal: { shouldShow: false },
    selectedAccount: null,
    selectedOrganizationWithPermissions: null,
    selectOrganization: null,
    lastAccessedAccounts: [],
    lastAccessedOrganizations: [],
    allowedOrganizations: null,
    organizationData: null,
    paymentSlipState: {
      count: 0,
      results: [],
      current_page: 1,
      pages: [],
    },
    modalPaymentSlipState: {
      count: 0,
      results: [],
      current_page: 1,
      pages: [],
    },
    paymentSlipUpdatedAt: { responseStatus: 0, date: "" },
    pendingPaymentSlipsCount: 0,
    donePaymentSlipsCount: 0,
    rejectPaymentSlipsCount: 0,
    invoiceState: {
      count: 0,
      results: [],
      current_page: 1,
      pages: [],
    },
    issuedInvoiceState: {
      count: 0,
      results: [],
      current_page: 1,
      pages: [],
    },
    exportedIssuedInvoice: null,
    exportedIssuedInvoiceState: {
      count: 0,
      results: [],
      current_page: 1,
      pages: [],
    },
    modalInvoiceState: {
      count: 0,
      results: [],
      current_page: 1,
      pages: [],
    },
    invoiceUpdatedAt: { responseStatus: 0, date: "" },
    pendingInvoicesCount: 0,
    doneInvoicesCount: 0,
    rejectInvoicesCount: 0,
    allPaidElements: [],
    attachedInvoices: [],
    attachedPaymentSlips: [],
    updatedInvoice: undefined,
  },
};

const globalStateSlice = createSlice({
  name: "globalState",
  initialState: initialState,
  reducers: {
    setToastActionId: (state, action: PayloadAction<string | undefined>) => {
      state.data.toastActionId = action.payload;
    },
    setToastState: (state, action: PayloadAction<TToast>) => {
      state.data.toast = action.payload;
    },
    setLogoutModalState: (
      state,
      action: PayloadAction<{ shouldShow: boolean }>
    ) => {
      state.data.logoutModal.shouldShow = action.payload.shouldShow;
    },
    setSelectedAccountState: (
      state,
      action: PayloadAction<{
        selectedAccount: TAccount;
        lastAccessedAccounts: TAccount[];
      }>
    ) => {
      const { selectedAccount, lastAccessedAccounts } = action.payload;

      const filteredResults = lastAccessedAccounts.filter(
        (account) => account.uuid !== selectedAccount.uuid
      );

      state.data.lastAccessedAccounts = [selectedAccount, ...filteredResults];

      state.data.selectedAccount = selectedAccount;
    },
    clearLastAccessedOrganizations: (state) => {
      state.data.lastAccessedOrganizations = [];
    },
    clearInvoices: (state) => {
      state.data.invoiceState = {
        count: 0,
        results: [],
        current_page: 1,
        pages: [],
      };
    },
    clearModalInvoices: (state) => {
      state.data.modalInvoiceState = {
        count: 0,
        results: [],
        current_page: 1,
        pages: [],
      };
    },
    clearPaymentSlips: (state) => {
      state.data.paymentSlipState = {
        count: 0,
        results: [],
        current_page: 1,
        pages: [],
      };
    },
    setModalPaymentSlips: (
      state: TInitialState,
      action: PayloadAction<{
        paymentSlips: TPaymentSlipData;
        entryStatus?: TInvoiceEntryStatus;
      }>
    ) => {
      state.data.modalPaymentSlipState = action.payload.paymentSlips;
    },
    clearModalPaymentSlips: (state: TInitialState) => {
      state.data.modalPaymentSlipState = {
        count: 0,
        results: [],
        current_page: 1,
        pages: [],
      };
    },
    setSelectedOrganizationWithPermissionsState: (
      state,
      action: PayloadAction<{
        selectedOrganization: TOrganization;
      }>
    ) => {
      const { selectedOrganization /* lastAccessedOrganizations */ } =
        action.payload;

      state.data.selectedOrganizationWithPermissions = selectedOrganization;
    },
    setSelectOrganizationState: (state) => {
      const localSelectOrganization = handleGetDataFromLocalStorage(
        localStorageConstants.SELECT_ORGANIZATION
      ) as TOrganization;

      const localLastAccessedOrganizations = handleGetDataFromLocalStorage(
        localStorageConstants.LAST_ACCESSED_ORGANIZATIONS
      ) as TOrganization[];

      if (localSelectOrganization) {
        state.data.selectOrganization = localSelectOrganization;
      }

      if (localLastAccessedOrganizations) {
        state.data.lastAccessedOrganizations = localLastAccessedOrganizations;
      }
    },
    setOrganizationsState: (
      state,
      action: PayloadAction<{ organizationData: TOrganizationData }>
    ) => {
      state.data.organizationData = action.payload.organizationData;
    },
    setAllPaidElements: (
      state,
      action: PayloadAction<{ allPaidElements: TPaidInstallments[] }>
    ) => {
      state.data.allPaidElements = action.payload.allPaidElements;
    },
    setInvoiceCounters: (
      state,
      action: PayloadAction<{
        counters: {
          pending_count: number;
          finished_count: number;
          declined_count: number;
        };
      }>
    ) => {
      state.data.pendingInvoicesCount = action.payload.counters.pending_count;
      state.data.doneInvoicesCount = action.payload.counters.finished_count;
      state.data.rejectInvoicesCount = action.payload.counters.declined_count;
    },
    setInvoices: (
      state,
      action: PayloadAction<{
        invoices: TInvoiceData;
        entryStatus?: TInvoiceEntryStatus;
      }>
    ) => {
      state.data.invoiceState = action.payload.invoices;
    },
    seTIssuedInvoice: (
      state: TInitialState,
      action: PayloadAction<{
        invoices: TIssuedInvoiceData;
        entryStatus?: TInvoiceEntryStatus;
      }>
    ) => {
      state.data.issuedInvoiceState = action.payload.invoices;
      // Optionally handle issued invoice counters here if needed
    },
    setExportedIssuedInvoices: (
      state: TInitialState,
      action: PayloadAction<{
        invoices: TExportIssuedInvoiceData;
        entryStatus?: TInvoiceEntryStatus;
      }>
    ) => {
      state.data.exportedIssuedInvoiceState = action.payload.invoices;
      // Optionally handle exported issued invoice counters here if needed
    },
    setExportedIssuedInvoice: (
      state: TInitialState,
      action: PayloadAction<{
        invoice: TIssuedInvoiceExport | null;
      }>
    ) => {
      state.data.exportedIssuedInvoice = action.payload.invoice;
      // Optionally handle exported issued invoice counters here if needed
    },
    setModalInvoices: (
      state: TInitialState,
      action: PayloadAction<{
        invoices: TInvoiceData;
        entryStatus?: TInvoiceEntryStatus;
      }>
    ) => {
      state.data.modalInvoiceState = action.payload.invoices;
      // Optionally handle modal invoice counters here if needed
    },
    setPaymentSlipCounters: (
      state,
      action: PayloadAction<{
        counters: {
          pending_count: number;
          finished_count: number;
          declined_count: number;
        };
      }>
    ) => {
      state.data.pendingPaymentSlipsCount =
        action.payload.counters.pending_count;
      state.data.donePaymentSlipsCount = action.payload.counters.finished_count;
      state.data.rejectPaymentSlipsCount =
        action.payload.counters.declined_count;
    },
    setPaymentSlips: (
      state,
      action: PayloadAction<{
        paymentSlips: TPaymentSlipData;
        entryStatus?: TInvoiceEntryStatus;
      }>
    ) => {
      state.data.paymentSlipState = action.payload.paymentSlips;
    },
    setAttachedInvoices: (state, action: PayloadAction<TInvoice[]>) => {
      state.data.attachedInvoices = action.payload;
    },
    setAttachedPaymentSlips: (state, action: PayloadAction<TPaymentSlip[]>) => {
      state.data.attachedPaymentSlips = action.payload;
    },
    setInvoicesUpdatedAt: (state, action: PayloadAction<TInvoiceUpdatedAt>) => {
      state.data.invoiceUpdatedAt = {
        responseStatus: action.payload.responseStatus,
        date: action.payload.date,
      };
    },
    setPaymentSlipsUpdatedAt: (
      state,
      action: PayloadAction<TPaymentSlipUpdatedAt>
    ) => {
      state.data.paymentSlipUpdatedAt = {
        responseStatus: action.payload.responseStatus,
        date: action.payload.date,
      };
    },
    setAllowedOrganizationsState: (
      state,
      action: PayloadAction<{
        allowedOrganizations: TOrganization[];
      }>
    ) => {
      const { allowedOrganizations } = action.payload;

      state.data.allowedOrganizations = allowedOrganizations;
    },
    setUpdatedInvoice: (state, action: PayloadAction<TInvoice>) => {
      state.data.updatedInvoice = action.payload;
    },

    setIsLoadingInvoices: (state, action: PayloadAction<boolean>) => {
      state.isLoadingInvoices = action.payload;
    },
    setIsLoadingIssuedInvoices: (state, action: PayloadAction<boolean>) => {
      state.isLoadingIssuedInvoices = action.payload;
    },
    setIsLoadingExportedIssuedInvoices: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isLoadingExportedIssuedInvoices = action.payload;
    },
    setIsLoadingPaymentSlips: (state, action: PayloadAction<boolean>) => {
      state.isLoadingPaymentSlips = action.payload;
    },
    setIsLoadingMemberships: (state, action: PayloadAction<boolean>) => {
      state.isLoadingMemberships = action.payload;
    },
    setIsLoadingModalInvoices: (state, action: PayloadAction<boolean>) => {
      state.isLoadingModalInvoices = action.payload;
    },
    setIsLoadingModalPaymentSlips: (state, action: PayloadAction<boolean>) => {
      state.isLoadingModalPaymentSlips = action.payload;
    },
    setIsRestoringQuerystring: (state, action: PayloadAction<boolean>) => {
      state.isRestoringQuerystring = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchInvoices.pending, (state) => {
        state.isLoadingInvoices = true;
      })
      .addCase(fetchInvoices.fulfilled, (state) => {
        state.isLoadingInvoices = false;
      })
      .addCase(fetchInvoices.rejected, (state) => {
        state.isLoadingInvoices = false;
      })

      .addCase(fetchIssuedInvoices.pending, (state) => {
        state.isLoadingIssuedInvoices = true;
      })
      .addCase(fetchIssuedInvoices.fulfilled, (state) => {
        state.isLoadingIssuedInvoices = false;
      })
      .addCase(fetchIssuedInvoices.rejected, (state) => {
        state.isLoadingIssuedInvoices = false;
      })

      .addCase(fetchExportedIssuedInvoices.pending, (state) => {
        state.isLoadingExportedIssuedInvoices = true;
      })
      .addCase(fetchExportedIssuedInvoices.fulfilled, (state) => {
        state.isLoadingExportedIssuedInvoices = false;
      })
      .addCase(fetchExportedIssuedInvoices.rejected, (state) => {
        state.isLoadingExportedIssuedInvoices = false;
      })

      .addCase(fetchExportedIssuedInvoice.pending, (state) => {
        state.isLoadingExportedIssuedInvoices = true;
      })
      .addCase(fetchExportedIssuedInvoice.fulfilled, (state) => {
        state.isLoadingExportedIssuedInvoices = false;
      })
      .addCase(fetchExportedIssuedInvoice.rejected, (state) => {
        state.isLoadingExportedIssuedInvoices = false;
      })

      .addCase(fetchPaymentSlips.pending, (state) => {
        state.isLoadingPaymentSlips = true;
      })
      .addCase(fetchPaymentSlips.fulfilled, (state) => {
        state.isLoadingPaymentSlips = false;
      })
      .addCase(fetchPaymentSlips.rejected, (state) => {
        state.isLoadingPaymentSlips = false;
      })

      .addCase(fetchMemberships.pending, (state) => {
        state.isLoadingMemberships = true;
      })
      .addCase(fetchMemberships.fulfilled, (state) => {
        state.isLoadingMemberships = false;
      })
      .addCase(fetchMemberships.rejected, (state) => {
        state.isLoadingMemberships = false;
      })

      .addCase(fetchModalPaymentSlips.pending, (state) => {
        state.isLoadingModalPaymentSlips = true;
      })
      .addCase(fetchModalPaymentSlips.fulfilled, (state) => {
        state.isLoadingModalPaymentSlips = false;
      })
      .addCase(fetchModalPaymentSlips.rejected, (state) => {
        state.isLoadingModalPaymentSlips = false;
      })

      .addCase(fetchModalInvoices.pending, (state) => {
        state.isLoadingModalInvoices = true;
      })
      .addCase(fetchModalInvoices.fulfilled, (state) => {
        state.isLoadingModalInvoices = false;
      })
      .addCase(fetchModalInvoices.rejected, (state) => {
        state.isLoadingModalInvoices = false;
      });
  },
});

export const {
  setToastState,
  setSelectedAccountState,
  setSelectedOrganizationWithPermissionsState,
  setSelectOrganizationState,
  setAllowedOrganizationsState,
  setAllPaidElements,
  setOrganizationsState,
  setInvoices,
  seTIssuedInvoice,
  setExportedIssuedInvoice,
  setExportedIssuedInvoices,
  setModalInvoices,
  clearModalInvoices,
  setModalPaymentSlips,
  clearModalPaymentSlips,
  setLogoutModalState,
  setInvoicesUpdatedAt,
  clearLastAccessedOrganizations,
  clearInvoices,
  clearPaymentSlips,
  setPaymentSlips,
  setAttachedInvoices,
  setAttachedPaymentSlips,
  setPaymentSlipsUpdatedAt,
  setInvoiceCounters,
  setPaymentSlipCounters,
  setToastActionId,
  setUpdatedInvoice,
  setIsLoadingExportedIssuedInvoices,
  setIsLoadingIssuedInvoices,
  setIsLoadingInvoices,
  setIsLoadingPaymentSlips,
  setIsLoadingMemberships,
  setIsLoadingModalInvoices,
  setIsLoadingModalPaymentSlips,
  setIsRestoringQuerystring,
} = globalStateSlice.actions;
export default globalStateSlice.reducer;
